import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { getApiQueryData, useApiQuery } from "../../../services/api";
import { useCurrentUser } from "../../user/hooks/useCurrentUser";

/**
 * Whether this account is in the practice arm: whether its Specialty has a
 * practice catalogue to offer.
 *
 * The catalogue IS the rollout. A Specialty enters the arm the moment its
 * payload is written (`pg-write-practice-catalogue.yml`), on every environment
 * at once, and no console rule has to follow. Before this, the arm was a
 * per-specialty GrowthBook rule (`PRACTICE_PROFILE_RELEVANCE`), and a catalogue
 * written to staging stayed invisible there until somebody typed its uid into
 * the console.
 *
 * THREE states, and `out` is not a quieter `pending`. Both render what the app
 * rendered before practices existed, but `pending` decides nothing: it is the
 * catalogue read still in flight, and the redirects that read the arm
 * (`usePracticeProfilingGate`, the topics gate in `AppRoutes`) must not fire on
 * it.
 *
 * There is no second arm. `relevance=specialty` — the control arm that served
 * the declared Specialty through the same surfaces — was removed: the question
 * it existed to answer is settled, relevance by specialty does not work, and
 * nothing is measured against it (SPEC §3 étape 8).
 */
export type PracticeArm = "pending" | "out" | "in";

type CatalogueOffer =
  | { readonly status: "unsettled" }
  | { readonly status: "settled"; readonly count: number };

/** Pure so the rule is assertable without a React harness. */
export function resolvePracticeArm(offer: CatalogueOffer): PracticeArm {
  if (offer.status === "unsettled") return "pending";
  return offer.count > 0 ? "in" : "out";
}

/**
 * The catalogue a Specialty offers, from the same PUBLIC read the signup slide,
 * the settings editor and the gate issue, so react-query answers all of them
 * with one request.
 *
 * `state: "error"` is not "never answered": a refetch that fails after a success
 * reports an error while the cache still holds the body
 * (`useOnboardingFlow`, `practiceOfferAnswered`). The cache is the last answer,
 * and an arm that flipped to `pending` on a failed focus refetch would retire
 * the practice surfaces under a reader already using them.
 */
function useCatalogueOffer(specialtyUid: string | null): CatalogueOffer {
  const offerQuery = useApiQuery(
    "/webapp/public/practice-profiles/offered",
    specialtyUid === null ? false : { query: { specialtyUid } }
  );
  if (specialtyUid === null) return { status: "settled", count: 0 };
  if (offerQuery.state === "success")
    return { status: "settled", count: offerQuery.body.docs.length };
  if (offerQuery.state !== "error") return { status: "unsettled" };
  const cached = getApiQueryData("/webapp/public/practice-profiles/offered", {
    query: { specialtyUid },
  });
  return cached === null
    ? { status: "unsettled" }
    : { status: "settled", count: cached.docs.length };
}

/**
 * The arm, for a signed-in reader. A reader with no account is `pending`, not
 * `out`: nothing about them is known yet. An account with no Specialty is `out`:
 * there is no catalogue to read.
 */
export function usePracticeArm(): PracticeArm {
  const { currentUser } = useCurrentUser();
  const specialtyUid =
    currentUser === null || currentUser.mainSpecialtyModel === null
      ? null
      : currentUser.mainSpecialtyModel.uid;
  const offer = useCatalogueOffer(specialtyUid);
  if (currentUser === null) return "pending";
  return resolvePracticeArm(offer);
}

/**
 * Whether the practice question offers the free-text way out — the slide at
 * signup, and the field that re-reads the words in settings.
 *
 * No `pending` state of its own, unlike the arm above. At signup the slide that
 * asks this comes after six others, long after the GrowthBook payload boot
 * started; in settings an unsettled payload just
 * hides the field for a frame, and the editor hands the saved words back
 * whether or not it rendered them.
 */
export function usePracticeDeclaration(): boolean {
  return useFeatureIsOn("PRACTICE_DECLARATION");
}

/**
 * The same arm, read BEFORE the account exists, on the Specialty declared in the
 * carousel. `""` before the specialty slide is `pending`: nothing is declared
 * yet, and `out` there would be a claim rather than an answer.
 */
export function useSignupPracticeArm(specialtyUid: string): PracticeArm {
  const offer = useCatalogueOffer(
    specialtyUid.length === 0 ? null : specialtyUid
  );
  if (specialtyUid.length === 0) return "pending";
  return resolvePracticeArm(offer);
}
